import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	AlertTriangle,
	Archive,
	CalendarDays,
	CheckCircle2,
	ClipboardList,
	Clock,
	Info,
	MessageSquare,
	Plus,
	RotateCcw,
	Trash2,
} from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";
import * as actionService from "@/api/services/actionService";
import { CreateActionModal } from "@/components/actions/CreateActionModal";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { UserAvatar } from "@/components/ui/user-avatar";
import { ChatDiscussion } from "@/components-shared/ChatDiscussion";
import { useActions } from "@/contexts/ActionsContext";
import { useApp } from "@/contexts/AppContext";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { useUsers } from "@/contexts/UsersContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useTerminology } from "@/hooks/useTerminology";
import { cn } from "@/lib/utils";
import type {
	Action,
	ActionComment,
	CreateActionPayload,
	UpdateActionPayload,
} from "@/types/actions";

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (d: Date) =>
	d.toLocaleDateString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric",
	});

const startOfDay = (d: Date) => {
	const copy = new Date(d);
	copy.setHours(0, 0, 0, 0);
	return copy;
};

const getDuePill = (
	action: Action,
): "overdue" | "due-today" | "due-soon" | null => {
	if (action.status !== "open") return null;
	const now = startOfDay(new Date());
	const due = startOfDay(action.dueDate);
	const diffDays = Math.round(
		(due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
	);
	if (diffDays < 0) return "overdue";
	if (diffDays === 0) return "due-today";
	if (diffDays <= 5) return "due-soon";
	return null;
};

// ── Action Row ────────────────────────────────────────────────────────────────

interface ActionRowProps {
	action: Action;
	perspective: "assignedToMe" | "assignedByMe" | "archived";
	canComplete: boolean;
	onComplete: (action: Action) => void;
	onReopen: (actionId: string) => void;
	onArchiveRequest: (action: Action) => void;
	onReinstate: (actionId: string) => void;
	onDiscuss: (action: Action) => void;
	onEdit?: (action: Action) => void;
	onView?: (action: Action) => void;
}

const ActionRow: React.FC<ActionRowProps> = ({
	action,
	perspective,
	canComplete,
	onComplete,
	onReopen,
	onArchiveRequest,
	onReinstate,
	onDiscuss,
	onEdit,
	onView,
}) => {
	const duePill = getDuePill(action);
	const { getAvatarUrlByName } = useUserPreferences();
	const { getUserById } = useUsers();
	const { getActionsTerm } = useTerminology();

	const resolveUserName = (userId: string) =>
		getUserById(userId)?.name ?? userId;

	const isArchived = action.status === "archived";
	const isComplete = action.status === "complete";

	const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
		const target = e.target as HTMLElement;
		if (target.closest("button")) return;
		if (
			perspective === "assignedByMe" &&
			onEdit &&
			!isComplete &&
			!isArchived
		) {
			onEdit(action);
		} else if (isArchived || isComplete) {
			onView?.(action);
		}
	};

	return (
		<TooltipProvider delayDuration={400}>
			<div
				className={cn(
					"flex items-start gap-3 p-4 border rounded-lg transition-colors",
					isComplete ? "bg-muted/30" : "bg-background",
					duePill === "overdue" &&
						!isComplete &&
						!isArchived &&
						"border-destructive/50 bg-destructive/5",
					(perspective === "assignedByMe" && !isComplete && !isArchived) ||
						isArchived ||
						isComplete
						? "hover:bg-accent/50 cursor-pointer"
						: "hover:bg-accent/30",
				)}
				onClick={handleCardClick}
			>
				{/* Content */}
				<div className="flex-1 min-w-0">
					<div className="flex items-start justify-between gap-2">
						<span
							className={cn(
								"font-medium text-sm",
								isComplete && "line-through text-muted-foreground",
							)}
						>
							{action.title}
						</span>
						<div className="flex items-center gap-1 flex-shrink-0">
							{duePill === "overdue" && (
								<Badge variant="destructive" className="text-xs">
									<AlertTriangle className="h-3 w-3 mr-1" />
									Overdue
								</Badge>
							)}
							{duePill === "due-today" && (
								<Badge
									variant="outline"
									className="text-xs border-orange-500 text-orange-600"
								>
									<Clock className="h-3 w-3 mr-1" />
									Due Today
								</Badge>
							)}
							{duePill === "due-soon" && (
								<Badge variant="warning" className="text-xs">
									Due Soon
								</Badge>
							)}
							{isComplete && (
								<Badge
									variant="outline"
									className="text-xs border-green-500 text-green-600"
								>
									<CheckCircle2 className="h-3 w-3 mr-1" />
									Complete
								</Badge>
							)}
							{isArchived && (
								<Badge variant="secondary" className="text-xs">
									Archived
								</Badge>
							)}
						</div>
					</div>

					{action.description && (
						<p className="text-xs text-muted-foreground mt-1 line-clamp-2">
							{action.description}
						</p>
					)}

					<div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-muted-foreground">
						<span className="flex items-center gap-1">
							<CalendarDays className="h-3 w-3" />
							Due {formatDate(action.dueDate)}
						</span>
						<span className="flex items-center gap-1">
							<UserAvatar
								name={resolveUserName(
									perspective === "assignedToMe"
										? action.assignedBy
										: action.assignedTo,
								)}
								seed={resolveUserName(
									perspective === "assignedToMe"
										? action.assignedBy
										: action.assignedTo,
								)}
								avatarUrl={getAvatarUrlByName(
									resolveUserName(
										perspective === "assignedToMe"
											? action.assignedBy
											: action.assignedTo,
									),
								)}
								size="xs"
							/>
							{perspective === "assignedToMe"
								? `Assigned by ${resolveUserName(action.assignedBy)}`
								: perspective === "archived"
									? `${resolveUserName(action.assignedTo)}`
									: `Assigned to ${resolveUserName(action.assignedTo)}`}
						</span>
						{action.relatedInvoiceRef && (
							<span className="text-xs bg-muted px-1.5 py-0.5 rounded">
								{action.relatedInvoiceRef}
							</span>
						)}
						{isArchived && action.archivedByName && (
							<span className="flex items-center gap-1 text-muted-foreground/70">
								<Archive className="h-3 w-3" />
								Archived by {action.archivedByName}
								{action.archivedAt && ` on ${formatDate(action.archivedAt)}`}
							</span>
						)}
					</div>

					{isComplete && action.completionComment && (
						<div className="mt-2 text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1.5 border-l-2 border-green-500">
							<span className="font-medium">Completed:</span>{" "}
							{action.completionComment}
							{action.completedAt && (
								<span className="ml-1 text-muted-foreground/70">
									— {formatDate(action.completedAt)}
								</span>
							)}
						</div>
					)}

					{perspective === "assignedByMe" && !isComplete && !isArchived && (
						<p className="text-[10px] text-muted-foreground/50 mt-1">
							Click to edit
						</p>
					)}
					{(isArchived || isComplete) && (
						<p className="text-[10px] text-muted-foreground/50 mt-1">
							Click to view details
						</p>
					)}
				</div>

				{/* Action buttons */}
				<div
					className="flex items-center gap-1 flex-shrink-0"
					onClick={(e) => e.stopPropagation()}
				>
					{/* Discussion */}
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="sm"
								className="h-7 px-1.5 text-muted-foreground hover:text-foreground relative"
								onClick={() => onDiscuss(action)}
							>
								<MessageSquare className="h-3.5 w-3.5" />
								{(action.messageCount ?? action.comments.length) > 0 && (
									<span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center leading-none">
										{action.messageCount ?? action.comments.length}
									</span>
								)}
							</Button>
						</TooltipTrigger>
						<TooltipContent>Discussion</TooltipContent>
					</Tooltip>

					{/* Complete button — only on open actions, and only if user has complete_actions permission */}
					{action.status === "open" && canComplete && (
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="sm"
									className="h-7 w-7 p-0 text-muted-foreground hover:text-green-600"
									onClick={() => onComplete(action)}
								>
									<CheckCircle2 className="h-3.5 w-3.5" />
								</Button>
							</TooltipTrigger>
							<TooltipContent>Mark as Complete</TooltipContent>
						</Tooltip>
					)}

					{/* Reopen — on completed actions */}
					{isComplete && (
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="sm"
									className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
									onClick={() => onReopen(action.id)}
								>
									<RotateCcw className="h-3.5 w-3.5" />
								</Button>
							</TooltipTrigger>
							<TooltipContent>Reopen</TooltipContent>
						</Tooltip>
					)}

					{/* Reinstate — on archived actions */}
					{isArchived && (
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="sm"
									className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
									onClick={() => onReinstate(action.id)}
								>
									<RotateCcw className="h-3.5 w-3.5" />
								</Button>
							</TooltipTrigger>
							<TooltipContent>Reinstate {getActionsTerm(true)}</TooltipContent>
						</Tooltip>
					)}

					{/* Archive — only on open actions */}
					{action.status === "open" && (
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="sm"
									className="h-7 w-7 p-0 text-muted-foreground hover:text-amber-600"
									onClick={() => onArchiveRequest(action)}
								>
									<Trash2 className="h-3.5 w-3.5" />
								</Button>
							</TooltipTrigger>
							<TooltipContent>Archive</TooltipContent>
						</Tooltip>
					)}
				</div>
			</div>
		</TooltipProvider>
	);
};

// ── View Detail Modal (archived / completed) ──────────────────────────────────

interface ViewModalProps {
	action: Action | null;
	onClose: () => void;
	onDiscuss: (action: Action) => void;
	onReinstate: (actionId: string) => void;
	onReopen: (actionId: string) => void;
}

const ViewModal: React.FC<ViewModalProps> = ({
	action,
	onClose,
	onDiscuss,
	onReinstate,
	onReopen,
}) => {
	const { getUserById } = useUsers();
	const { getAvatarUrlByName } = useUserPreferences();

	if (!action) return null;

	const isArchived = action.status === "archived";
	const isComplete = action.status === "complete";

	const resolveUserName = (userId: string) =>
		getUserById(userId)?.name ?? userId;

	return (
		<Dialog open={!!action} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						{isArchived ? (
							<Archive className="h-5 w-5 text-muted-foreground" />
						) : (
							<CheckCircle2 className="h-5 w-5 text-green-600" />
						)}
						{isArchived ? "Archived Action" : "Completed Action"}
					</DialogTitle>
				</DialogHeader>

				<div className="space-y-4 py-1">
					{/* Title */}
					<div>
						<p className="text-base font-semibold">{action.title}</p>
						{action.description && (
							<p className="text-sm text-muted-foreground mt-1">
								{action.description}
							</p>
						)}
					</div>

					{/* Meta grid */}
					<div className="grid grid-cols-2 gap-3 text-sm">
						<div className="space-y-1">
							<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
								Assigned to
							</p>
							<div className="flex items-center gap-2">
								<UserAvatar
									name={resolveUserName(action.assignedTo)}
									seed={resolveUserName(action.assignedTo)}
									avatarUrl={getAvatarUrlByName(
										resolveUserName(action.assignedTo),
									)}
									size="xs"
								/>
								<span>{resolveUserName(action.assignedTo)}</span>
							</div>
						</div>
						<div className="space-y-1">
							<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
								Assigned by
							</p>
							<div className="flex items-center gap-2">
								<UserAvatar
									name={resolveUserName(action.assignedBy)}
									seed={resolveUserName(action.assignedBy)}
									avatarUrl={getAvatarUrlByName(
										resolveUserName(action.assignedBy),
									)}
									size="xs"
								/>
								<span>{resolveUserName(action.assignedBy)}</span>
							</div>
						</div>
						<div className="space-y-1">
							<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
								Due date
							</p>
							<p className="flex items-center gap-1.5">
								<CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
								{formatDate(action.dueDate)}
							</p>
						</div>
						{action.relatedInvoiceRef && (
							<div className="space-y-1">
								<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
									Invoice ref
								</p>
								<p className="text-xs bg-muted px-1.5 py-0.5 rounded inline-block">
									{action.relatedInvoiceRef}
								</p>
							</div>
						)}
					</div>

					{/* Completion note */}
					{isComplete && action.completionComment && (
						<div className="rounded-md border-l-2 border-green-500 bg-muted/50 px-3 py-2 text-sm">
							<p className="text-xs font-medium text-muted-foreground mb-0.5">
								Completion note
							</p>
							<p>{action.completionComment}</p>
							{action.completedAt && (
								<p className="text-xs text-muted-foreground mt-1">
									Completed {formatDate(action.completedAt)}
								</p>
							)}
						</div>
					)}

					{/* Archive note */}
					{isArchived && action.archivedByName && (
						<div className="rounded-md border-l-2 border-muted-foreground/30 bg-muted/50 px-3 py-2 text-sm">
							<p className="text-xs font-medium text-muted-foreground mb-0.5">
								Archived by
							</p>
							<p>
								{action.archivedByName}
								{action.archivedAt && ` on ${formatDate(action.archivedAt)}`}
							</p>
						</div>
					)}
				</div>

				<DialogFooter className="flex-wrap gap-2 sm:justify-between">
					<Button
						variant="ghost"
						size="sm"
						className="text-muted-foreground"
						onClick={() => {
							onClose();
							onDiscuss(action);
						}}
					>
						<MessageSquare className="h-4 w-4 mr-2" />
						Discussion
						{(action.messageCount ?? action.comments.length) > 0 && (
							<span className="ml-1.5 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center">
								{action.messageCount ?? action.comments.length}
							</span>
						)}
					</Button>
					<div className="flex gap-2">
						<Button variant="outline" onClick={onClose}>
							Close
						</Button>
						{isArchived && (
							<Button
								variant="outline"
								onClick={() => {
									onReinstate(action.id);
									onClose();
								}}
							>
								<RotateCcw className="h-4 w-4 mr-2" />
								Reinstate
							</Button>
						)}
						{isComplete && (
							<Button
								variant="outline"
								onClick={() => {
									onReopen(action.id);
									onClose();
								}}
							>
								<RotateCcw className="h-4 w-4 mr-2" />
								Reopen
							</Button>
						)}
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

// ── Complete Action Modal ─────────────────────────────────────────────────────

interface CompleteModalProps {
	action: Action | null;
	onConfirm: (comment: string) => void;
	onClose: () => void;
}

const CompleteModal: React.FC<CompleteModalProps> = ({
	action,
	onConfirm,
	onClose,
}) => {
	const [comment, setComment] = useState("");
	const { getUserById } = useUsers();

	if (!action) return null;

	return (
		<Dialog open={!!action} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<CheckCircle2 className="h-5 w-5 text-green-600" />
						Mark as Complete
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-4 py-2">
					<div className="text-sm font-medium">{action.title}</div>
					<div className="space-y-2">
						<Label htmlFor="completion-comment">
							Completion comment <span className="text-destructive">*</span>
						</Label>
						<Textarea
							id="completion-comment"
							placeholder="Briefly describe what was done or the outcome…"
							value={comment}
							onChange={(e) => setComment(e.target.value)}
							rows={3}
							className="resize-none"
						/>
						<p className="text-xs text-muted-foreground">
							This will be shared with{" "}
							{getUserById(action.assignedBy)?.name ?? action.assignedBy} who
							assigned this task.
						</p>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button
						onClick={() => {
							if (!comment.trim()) return;
							onConfirm(comment.trim());
						}}
						disabled={!comment.trim()}
						className="bg-teal-600 hover:bg-teal-700 text-white"
					>
						Mark Complete
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

// ── Discussion Modal ──────────────────────────────────────────────────────────

interface DiscussionModalProps {
	action: Action | null;
	currentUserId: string;
	onPostComment: (actionId: string, text: string) => void;
	onClose: () => void;
	isLiveMode: boolean;
}

const DiscussionModal: React.FC<DiscussionModalProps> = ({
	action,
	currentUserId,
	onPostComment,
	onClose,
	isLiveMode,
}) => {
	const [isSending, setIsSending] = useState(false);
	const queryClient = useQueryClient();

	const numericActionId = action ? Number(action.id) : 0;

	// Live mode: fetch messages via GraphQL
	const { data: liveMessagesData, isLoading: isLoadingMessages } = useQuery({
		queryKey: ["actionMessages", numericActionId],
		queryFn: () =>
			actionService.getActionMessageViewList({
				actionId: { eq: numericActionId },
			}),
		enabled: isLiveMode && !!action,
		staleTime: 10_000,
	});

	// Map live messages to ActionComment shape for unified rendering
	const liveComments: ActionComment[] = React.useMemo(() => {
		if (!liveMessagesData?.edges) return [];
		return liveMessagesData.edges.map((edge) => ({
			id: String(edge.node.id),
			actionId: String(edge.node.actionId),
			authorId: edge.node.authorId,
			authorName: edge.node.authorName ?? edge.node.authorId,
			authorRole: "",
			text: edge.node.message,
			createdAt: new Date(edge.node.dateCreated),
		}));
	}, [liveMessagesData]);

	// Use live messages in live mode, demo comments in demo mode
	const comments = isLiveMode ? liveComments : (action?.comments ?? []);

	if (!action) return null;

	const handleSend = async (text: string) => {
		if (isLiveMode) {
			setIsSending(true);
			try {
				await actionService.addActionMessage(numericActionId, {
					message: text,
				});
				// Refresh the messages list and actions (for messageCount badge)
				queryClient.invalidateQueries({
					queryKey: ["actionMessages", numericActionId],
				});
				queryClient.invalidateQueries({ queryKey: ["actions"] });
			} finally {
				setIsSending(false);
			}
		} else {
			onPostComment(action.id, text);
		}
	};

	return (
		<Dialog open={!!action} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<MessageSquare className="h-5 w-5 text-muted-foreground" />
						Discussion
					</DialogTitle>
					<p className="text-sm text-muted-foreground line-clamp-1 mt-1">
						{action.title}
					</p>
				</DialogHeader>

				<ChatDiscussion
					messages={comments}
					currentUserId={currentUserId}
					onSend={handleSend}
					isSending={isSending}
					isLoading={isLiveMode && isLoadingMessages}
					placeholder="Type a message… (Enter to send)"
					height="h-80"
				/>
			</DialogContent>
		</Dialog>
	);
};

// ── Main Page ─────────────────────────────────────────────────────────────────

export const ActionsPage: React.FC = () => {
	const { getActionsTerm } = useTerminology();
	const { currentUser, mode } = useApp();
	const { hasPermission } = usePermissions();
	const canCreateActions = hasPermission("create_actions");
	const canCompleteActions = hasPermission("complete_actions");
	const {
		actions,
		effectiveUserId,
		createAction,
		updateAction,
		completeAction,
		reopenAction,
		archiveAction,
		reinstateAction,
		addActionComment,
	} = useActions();
	const { getUserById } = useUsers();

	const [completeTarget, setCompleteTarget] = useState<Action | null>(null);
	const [archiveConfirmTarget, setArchiveConfirmTarget] =
		useState<Action | null>(null);
	const [createOpen, setCreateOpen] = useState(false);
	const [editTarget, setEditTarget] = useState<Action | null>(null);
	const [discussTarget, setDiscussTarget] = useState<Action | null>(null);
	const [viewTarget, setViewTarget] = useState<Action | null>(null);

	// Keep discussion modal in sync with latest action state
	const discussAction = discussTarget
		? (actions.find((a) => a.id === discussTarget.id) ?? null)
		: null;

	const actionsTerm = getActionsTerm();

	// Split by perspective
	const assignedToMe = actions.filter(
		(a) => a.assignedTo === effectiveUserId && a.status === "open",
	);
	const assignedByMe = actions.filter(
		(a) => a.assignedBy === effectiveUserId && a.status === "open",
	);

	// Archived tab — all archived + completed, deduped, newest first
	const archivedAndCompleted = actions
		.filter(
			(a) =>
				(a.assignedTo === effectiveUserId ||
					a.assignedBy === effectiveUserId) &&
				(a.status === "complete" || a.status === "archived"),
		)
		.filter((a, i, arr) => arr.findIndex((x) => x.id === a.id) === i)
		.sort((a, b) => {
			const aDate = a.completedAt ?? a.archivedAt ?? a.createdAt;
			const bDate = b.completedAt ?? b.archivedAt ?? b.createdAt;
			return bDate.getTime() - aDate.getTime();
		});

	const handleComplete = (action: Action) => setCompleteTarget(action);

	const handleCompleteConfirm = (comment: string) => {
		if (!completeTarget) return;
		completeAction({ actionId: completeTarget.id, completionComment: comment });
		toast.success(`${actionsTerm} marked as complete`);
		setCompleteTarget(null);
	};

	const handleCreate = (payload: CreateActionPayload) => {
		createAction(payload);
		toast.success(
			`${actionsTerm} assigned to ${getUserById(payload.assignedTo)?.name ?? payload.assignedTo}`,
		);
		setCreateOpen(false);
	};

	const handleUpdate = (payload: UpdateActionPayload) => {
		const original = actions.find((a) => a.id === payload.actionId);
		const isReassigned = original && original.assignedTo !== payload.assignedTo;

		if (isReassigned && original) {
			// Archive original, create new action for new assignee
			archiveAction(original.id);
			createAction({
				title: payload.title,
				description: payload.description,
				assignedTo: payload.assignedTo,
				dueDate: payload.dueDate,
				relatedInvoiceId: original.relatedInvoiceId,
				relatedInvoiceRef: original.relatedInvoiceRef,
				relatedCustomer: original.relatedCustomer,
			});
			toast.success(
				`${actionsTerm} reassigned — original record archived and new record created`,
			);
		} else {
			updateAction(payload);
			toast.success(`${actionsTerm} updated`);
		}
		setEditTarget(null);
	};

	const handleArchiveRequest = (action: Action) => {
		setArchiveConfirmTarget(action);
	};

	const handleArchiveConfirm = () => {
		if (!archiveConfirmTarget) return;
		archiveAction(archiveConfirmTarget.id);
		toast.success(`${actionsTerm} archived`);
		setArchiveConfirmTarget(null);
	};

	const handleReinstate = (actionId: string) => {
		reinstateAction(actionId);
		toast.success(`${actionsTerm} reinstated`);
	};

	const handleReopen = (actionId: string) => {
		reopenAction(actionId);
		toast.success(`${actionsTerm} reopened`);
	};

	const EmptyState: React.FC<{ message: string }> = ({ message }) => (
		<div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
			<ClipboardList className="h-10 w-10 mb-3 opacity-30" />
			<p className="text-sm">{message}</p>
		</div>
	);

	return (
		<div className="p-6 space-y-6 bg-muted/40 min-h-screen">
			{/* Tabs */}
			<Tabs defaultValue="assigned-to-me">
				<div className="flex items-center justify-between">
				<TabsList>
					<TabsTrigger
						value="assigned-to-me"
						className="flex items-center gap-2"
					>
						Assigned to Me
						{assignedToMe.length > 0 && (
							<Badge variant="secondary" className="h-5 px-1.5 text-xs">
								{assignedToMe.length}
							</Badge>
						)}
					</TabsTrigger>
					<TabsTrigger
						value="assigned-by-me"
						className="flex items-center gap-2"
					>
						Assigned by Me
						{assignedByMe.length > 0 && (
							<Badge variant="secondary" className="h-5 px-1.5 text-xs">
								{assignedByMe.length}
							</Badge>
						)}
					</TabsTrigger>
					<TabsTrigger value="archived" className="flex items-center gap-2">
						Archived & Completed
						{archivedAndCompleted.length > 0 && (
							<Badge variant="secondary" className="h-5 px-1.5 text-xs">
								{archivedAndCompleted.length}
							</Badge>
						)}
					</TabsTrigger>
				</TabsList>
				{canCreateActions && (
					<Button onClick={() => setCreateOpen(true)}>
						<Plus className="h-4 w-4 mr-2" />
						New {actionsTerm}
					</Button>
				)}
				</div>

				{/* ── Assigned To Me ── */}
				<TabsContent value="assigned-to-me" className="mt-4 space-y-4">
					<Card>
						<CardHeader className="pb-3">
							<CardTitle className="text-base">
								Open {actionsTerm.toLowerCase()}
								{assignedToMe.length > 0 && (
									<span className="ml-2 text-muted-foreground font-normal text-sm">
										({assignedToMe.length})
									</span>
								)}
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-2">
							{assignedToMe.length === 0 ? (
								<EmptyState
									message={`You're all caught up — no open ${actionsTerm.toLowerCase()} assigned to you.`}
								/>
							) : (
								assignedToMe
									.sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime())
									.map((action) => (
										<ActionRow
											key={action.id}
											action={action}
											perspective="assignedToMe"
											canComplete={canCompleteActions}
											onComplete={handleComplete}
											onReopen={handleReopen}
											onArchiveRequest={handleArchiveRequest}
											onReinstate={handleReinstate}
											onDiscuss={setDiscussTarget}
											onView={setViewTarget}
										/>
									))
							)}
						</CardContent>
					</Card>
				</TabsContent>

				{/* ── Assigned By Me ── */}
				<TabsContent value="assigned-by-me" className="mt-4 space-y-4">
					<Card>
						<CardHeader className="pb-3">
							<CardTitle className="text-base">
								Open {actionsTerm.toLowerCase()}
								{assignedByMe.length > 0 && (
									<span className="ml-2 text-muted-foreground font-normal text-sm">
										({assignedByMe.length})
									</span>
								)}
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-2">
							{assignedByMe.length === 0 ? (
								<EmptyState
									message={`You have no open ${actionsTerm.toLowerCase()} assigned to others.`}
								/>
							) : (
								assignedByMe
									.sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime())
									.map((action) => (
										<ActionRow
											key={action.id}
											action={action}
											perspective="assignedByMe"
											canComplete={canCompleteActions}
											onComplete={handleComplete}
											onReopen={handleReopen}
											onArchiveRequest={handleArchiveRequest}
											onReinstate={handleReinstate}
											onDiscuss={setDiscussTarget}
											onEdit={setEditTarget}
											onView={setViewTarget}
										/>
									))
							)}
						</CardContent>
					</Card>
				</TabsContent>

				{/* ── Archived ── */}
				<TabsContent value="archived" className="mt-4 space-y-4">
					{/* Retention notice — consistent with Reports 30-day pattern */}
					<div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30 px-4 py-3 text-sm text-blue-800 dark:text-blue-300">
						<Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
						<span>
							Archived and completed {actionsTerm.toLowerCase()} are retained
							for <strong>12 months</strong> and then permanently removed.
						</span>
					</div>
					<Card>
						<CardHeader className="pb-3">
							<CardTitle className="text-base">
								Archived & Completed {actionsTerm}
								{archivedAndCompleted.length > 0 && (
									<span className="ml-2 text-muted-foreground font-normal text-sm">
										({archivedAndCompleted.length})
									</span>
								)}
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-2">
							{archivedAndCompleted.length === 0 ? (
								<EmptyState
									message={`No archived or completed ${actionsTerm.toLowerCase()} yet.`}
								/>
							) : (
								archivedAndCompleted.map((action) => (
									<ActionRow
										key={action.id}
										action={action}
										perspective={
											action.status === "archived"
												? "archived"
												: action.assignedTo === currentUser.id
													? "assignedToMe"
													: "assignedByMe"
										}
										canComplete={canCompleteActions}
										onComplete={handleComplete}
										onReopen={handleReopen}
										onArchiveRequest={handleArchiveRequest}
										onReinstate={handleReinstate}
										onDiscuss={setDiscussTarget}
										onView={setViewTarget}
									/>
								))
							)}
						</CardContent>
					</Card>
				</TabsContent>
			</Tabs>

			{/* Modals */}
			<CompleteModal
				action={completeTarget}
				onConfirm={handleCompleteConfirm}
				onClose={() => setCompleteTarget(null)}
			/>

			{/* Archive confirmation */}
			<AlertDialog
				open={!!archiveConfirmTarget}
				onOpenChange={(open) => {
					if (!open) setArchiveConfirmTarget(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle className="flex items-center gap-2">
							<Trash2 className="h-5 w-5 text-teal-600" />
							Archive {actionsTerm}?
						</AlertDialogTitle>
						<AlertDialogDescription>
							Archive{" "}
							<span className="font-semibold">
								"{archiveConfirmTarget?.title}"
							</span>
							? It will be moved to the Archived tab and retained for 12 months.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setArchiveConfirmTarget(null)}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleArchiveConfirm}
							className="bg-teal-600 hover:bg-teal-700"
						>
							Archive
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
			{canCreateActions && (
				<CreateActionModal
					open={createOpen}
					onConfirm={handleCreate}
					onClose={() => setCreateOpen(false)}
					variant="action"
				/>
			)}
			{canCreateActions && (
				<CreateActionModal
					open={!!editTarget}
					editAction={editTarget ?? undefined}
					onConfirm={handleCreate}
					onUpdate={handleUpdate}
					onClose={() => setEditTarget(null)}
					variant="action"
				/>
			)}
			<DiscussionModal
				action={discussAction}
				currentUserId={effectiveUserId}
				onPostComment={addActionComment}
				onClose={() => setDiscussTarget(null)}
				isLiveMode={mode === "live"}
			/>
			<ViewModal
				action={viewTarget}
				onClose={() => setViewTarget(null)}
				onDiscuss={(action) => {
					setViewTarget(null);
					setDiscussTarget(action);
				}}
				onReinstate={(id) => {
					handleReinstate(id);
				}}
				onReopen={(id) => {
					handleReopen(id);
				}}
			/>
		</div>
	);
};
